import {
  AppShell,
  createTheme,
  CSSVariablesResolver,
  DEFAULT_THEME,
  MantineProvider,
  mergeMantineTheme,
  Stack,
} from "@mantine/core"
import "@mantine/core/styles.css"
import { useEffect } from "react"

import {
  CommunicationStudioClasses,
  CUSTOMISATION_CATEGORIES,
  SETTINGS_SCOPE,
} from "@tfg/types/constants/communication-studio.constant"
import {
  LanguageIso6391,
  ProductSheetLanguage,
} from "@tfg/types/constants/language.constants"
import { AGECResponses } from "@tfg/types/endpoints/agec"
import { ProductSheetComponent } from "@tfg/types/schemas/communication-studio/components"
import { ScopedConfigurationsResponse } from "@tfg/types/types/communication-studio/configurations.types"
import { StylingParameters } from "@tfg/types/types/communication-studio/styling-parameters.types"

import styles from "./Generic.module.css"
import { GenericComponentsAGECPage } from "./GenericComponentsAGECPage"
import { DEFAULT_STYLING_PARAMETERS } from "./config"
import { StylingParametersProvider } from "./context/stylingParameters/stylingParametersProvider"
import { TranslationsProvider } from "./context/translationsContext/TranslationsProvider"
import { JonakAGECPage } from "./jonak/JonakAGECPage"
import { LangSwitcher } from "./ui/LangSwitcher"
import { DesignStudioParametersToolkit } from "./utils/designStudioParametersToolkit"

type Props = {
  agecData: AGECResponses.GenericAgecData
  onLanguageChange: (lang: string) => void
  translationFunction: (key: string, options?: Record<string, string>) => string
  currentLanguage: string
  activeLanguages: ProductSheetLanguage[]
  stylingParameters: StylingParameters
  configuration: ScopedConfigurationsResponse
  productPageComponents: ProductSheetComponent[]
  getLanguageName: (lang: LanguageIso6391) => string
}

type LegacyPageComponentProps = {
  agecData: Props["agecData"]
  t: Props["translationFunction"]
  currentLanguage: Props["currentLanguage"]
  logoUrl: string | null
}

const LegacyPagesMap: Record<
  string,
  React.ComponentType<LegacyPageComponentProps>
> = {
  jonak: JonakAGECPage,
}

export function PageContainer({
  agecData,
  onLanguageChange,
  translationFunction,
  currentLanguage,
  activeLanguages,
  stylingParameters,
  configuration,
  productPageComponents,
  getLanguageName,
}: Props) {
  const {
    fontFamily,
    headerFontWeight,
    headerFontSize,
    textFontSize,
    lineHeight,
    paddingBlock,
  } = DesignStudioParametersToolkit.getGlobalThemeSettings(
    stylingParameters.global
  )
  useEffect(() => {
    DesignStudioParametersToolkit.injectAllClassesToDocumentHead(
      stylingParameters
    )
    DesignStudioParametersToolkit.injectCssVariablesToDocumentHead(
      stylingParameters
    )
  }, [stylingParameters])

  const themeOverride = createTheme({
    fontFamily,
    scale: 1,
    headings: {
      fontWeight: headerFontWeight,
      fontFamily,
      sizes: {
        h3: {
          fontSize: headerFontSize,
        },
      },
    },
    fontSizes: {
      sm: textFontSize,
    },
    spacing: {
      sm: paddingBlock,
    },
    lineHeights: {
      xs: lineHeight,
      sm: lineHeight,
      md: lineHeight,
    },
  })

  const theme = mergeMantineTheme(DEFAULT_THEME, themeOverride)
  const resolver: CSSVariablesResolver = () => ({
    variables: {
      "--mantine-line-height": lineHeight ?? "18px",
    },
    dark: {},
    light: {
      "--background":
        stylingParameters.global?.backgroundColor ??
        DEFAULT_STYLING_PARAMETERS.global?.backgroundColor,
    },
  })

  const LegacyPageComponent = LegacyPagesMap[agecData.organizationName]

  const legacyPage = LegacyPageComponent ? (
    <LegacyPageComponent
      agecData={agecData}
      t={translationFunction}
      currentLanguage={currentLanguage}
      logoUrl={
        agecData.organizationName === "ikks"
          ? (configuration[SETTINGS_SCOPE.CONTENT_FILTERING].logoFile?.url ??
            null)
          : null
      }
    />
  ) : null

  return (
    <StylingParametersProvider
      stylingParameters={stylingParameters}
      configurations={configuration}
    >
      <TranslationsProvider
        t={translationFunction}
        onLanguageChange={onLanguageChange}
        currentLanguage={currentLanguage}
      >
        <MantineProvider theme={theme} cssVariablesResolver={resolver}>
          <div
            id="root"
            style={{
              height: "100vh",
              overflow: "auto",
              ...DesignStudioParametersToolkit.spreadStylingParameters({
                category: CUSTOMISATION_CATEGORIES.GLOBAL,
                stylingParameters,
                stylingParamKeys: ["backgroundColor"],
              }),
            }}
          >
            <AppShell
              withBorder={false}
              header={{
                height: { base: 40, sm: 34, lg: 48 },
                offset: false,
              }}
              styles={{
                root: {
                  justifyItems: "center",
                  alignItems: "center",
                },
                header: {
                  width: "100%",
                  position: "sticky",
                },
              }}
            >
              <AppShell.Header
                className={CommunicationStudioClasses.APP_HEADER}
              >
                <LangSwitcher
                  languages={activeLanguages}
                  style={{
                    float: "right",
                  }}
                  onLanguageChange={onLanguageChange}
                  getLanguageName={getLanguageName}
                />
              </AppShell.Header>
              <div className={styles.page}>
                {legacyPage ? (
                  legacyPage
                ) : (
                  <Stack
                    justify="center"
                    align="center"
                    className={`${CommunicationStudioClasses.MAIN_CONTAINER} `}
                    style={{
                      ...DesignStudioParametersToolkit.spreadStylingParameters({
                        category: CUSTOMISATION_CATEGORIES.GLOBAL,
                        stylingParameters,
                        stylingParamKeys: ["width", "height", "paddingBlock"],
                      }),
                      overflow: "hidden",
                    }}
                  >
                    <GenericComponentsAGECPage
                      agecData={agecData}
                      configuration={configuration}
                      productPageComponents={productPageComponents}
                    />
                  </Stack>
                )}
              </div>
            </AppShell>
          </div>
        </MantineProvider>
      </TranslationsProvider>
    </StylingParametersProvider>
  )
}
